import { useState, useEffect } from 'react';
import logoImage from '@/assets/images/logo_re.png';
import ftLogoImage from '@/assets/images/ft_logo.png';

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={scrolled ? logoImage : ftLogoImage}
                alt="グッドサービス"
                className="h-[38px] w-auto object-contain"
              />
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className={`text-sm font-medium hover:text-primary transition-colors cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>サービス</a>
              <a href="#reasons" className={`text-sm font-medium hover:text-primary transition-colors cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>選ばれる理由</a>
              <a href="#projects" className={`text-sm font-medium hover:text-primary transition-colors cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>実績</a>
              <a href="#testimonials" className={`text-sm font-medium hover:text-primary transition-colors cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>お客様の声</a>
              <a href="#contact" className={`text-sm font-medium hover:text-primary transition-colors cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}>お問い合わせ</a>
              <a 
              href="tel:0120-952-005"
              className="px-6 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-pink-600 transition-all whitespace-nowrap cursor-pointer flex flex-col items-center leading-tight"
            >
              <span className="text-lg font-bold">0120-952-005</span>
              <span className="text-[10px] font-normal opacity-90">9:00-20:00 年中無休</span>
            </a>
            </div>

            <button className="md:hidden">
              <i className={`ri-menu-line text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-black"></div>
          <video
            className="absolute top-1/2 left-1/2 min-w-full min-h-full -translate-x-1/2 -translate-y-1/2 pointer-events-none object-cover"
            src={`${import.meta.env.BASE_URL}mv.mp4`}
            autoPlay
            muted
            loop
            playsInline
          />
          {/* グラデーションオーバーレイ */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          {/* ドットパターンオーバーレイ */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          ></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 py-40 text-center">

          {/* キャッチバッジ */}
          <div className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-sm border border-primary/40 px-5 py-2 rounded-full mb-10">
            <i className="ri-shield-check-line text-primary"></i>
            <span className="text-pink-100 font-semibold text-sm tracking-wide">秘密厳守・最短即日対応・見積もり無料</span>
          </div>

          {/* メインコピー */}
          <h2 className="text-5xl md:text-7xl font-black text-white mb-4 leading-[1.15] tracking-tight drop-shadow-lg">
            ゴミ屋敷の悩み、
          </h2>
          <h2 className="text-5xl md:text-7xl font-black leading-[1.15] tracking-tight drop-shadow-lg mb-8">
            <span className="text-primary">プロが解決します。</span>
          </h2>

          {/* サブコピー */}
          <p className="text-lg md:text-xl text-gray-200 mb-14 max-w-2xl mx-auto leading-relaxed">
            一人で悩まずご相談ください。ゴミ屋敷清掃、遺品整理、不用品回収。<br />
            経験豊富なスタッフが、あなたのお部屋を元の快適な空間に戻します。
          </p>

          {/* CTAボタン：同サイズ横並び */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button
              onClick={scrollToContact}
              className="group w-full sm:w-auto flex flex-col items-center justify-center gap-0.5 px-8 py-5 bg-primary text-white rounded-xl hover:bg-pink-600 transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <i className="ri-mail-send-line text-xl"></i>
                <span className="text-2xl font-black">無料お見積りはこちら</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </div>
              <span className="text-xs font-normal opacity-80">お見積り・ご相談は完全無料です</span>
            </button>
            <a
              href="tel:0120-952-005"
              className="group w-full sm:w-auto flex flex-col items-center justify-center gap-0.5 px-8 py-5 bg-white/95 text-gray-900 rounded-xl hover:bg-white transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <i className="ri-phone-fill text-primary text-xl"></i>
                <span className="text-2xl font-black tracking-wider">0120-952-005</span>
              </div>
              <span className="text-xs font-normal text-gray-500">9:00-20:00 ／ 年中無休（年末年始・お盆除く）</span>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-5 border border-white/20">
              <div className="text-3xl font-black text-primary mb-1">10年+</div>
              <div className="text-xs text-gray-300 tracking-wide">信頼と実績</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-5 border border-white/20">
              <div className="text-3xl font-black text-primary mb-1">5,000件+</div>
              <div className="text-xs text-gray-300 tracking-wide">清掃実績</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-5 border border-white/20">
              <div className="text-3xl font-black text-primary mb-1">年中無休</div>
              <div className="text-xs text-gray-300 tracking-wide">年末年始・お盆除く</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-5 border border-white/20">
              <div className="text-3xl font-black text-primary mb-1">99%</div>
              <div className="text-xs text-gray-300 tracking-wide">お客様満足度</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <i className="ri-arrow-down-s-line text-4xl text-white/70"></i>
        </div>
      </section>
    </>
  );
}